import type {
  ApiDashboardElement,
  SourceMap
} from '@highimpact-frontend/api-types';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useDashboardHost, useDashboardScope } from '../context/DashboardHostContext';
import {
  elementNeedsSourceLabelResolution,
  getMeasurePointPathsFromElement,
} from '../state/hydrationSourceLabels';
import {
  EMPTY_SOURCE_MAP,
  fetchSourceLabelMapForElements,
} from '../state/resolveSourceLabels';
import { dashboardQueryKeys } from './queryKeys';
import { getSourceLabelPairsKey } from './sourceLabelQueryUtils';

export interface UseSourceLabelsQueryResult {
  readonly labelMap: SourceMap;
  readonly loading: boolean;
  readonly error: Error | null;
}

/**
 * Resolves measure source display labels for a dashboard element.
 *
 * Uses persisted `data_source_to_axis` names when complete; otherwise fetches
 * via {@link SourceLabelsApi.getLabels}.
 */
export interface UseSourceLabelsQueryOptions {
  readonly enabled?: boolean;
}

export function useSourceLabelsQuery(
  element: ApiDashboardElement,
  options: UseSourceLabelsQueryOptions = {}
): UseSourceLabelsQueryResult {
  const { enabled = true } = options;
  const host = useDashboardHost();
  const scope = useDashboardScope();

  const pairs = useMemo(
    () => getMeasurePointPathsFromElement(element),
    [element]
  );
  const needsRemoteLabels = elementNeedsSourceLabelResolution(element);

  const query = useQuery({
    queryKey: dashboardQueryKeys.sourceLabels(
      scope.project_id,
      scope.site_id,
      pairs
    ),
    queryFn: () =>
      fetchSourceLabelMapForElements(
        host.api.sources.getLabels,
        [element],
        {
          project_id: scope.project_id ?? undefined,
          site_id: scope.site_id ?? undefined,
        }
      ),
    enabled: enabled && needsRemoteLabels && pairs.length > 0,
    staleTime: 60_000,
  });

  const labelMap = query.data ?? EMPTY_SOURCE_MAP;

  return {
    labelMap,
    loading:
      enabled && needsRemoteLabels && pairs.length > 0 && query.isPending,
    error:
      query.error instanceof Error
        ? query.error
        : query.error
          ? new Error(String(query.error))
          : null,
  };
}

export { getSourceLabelPairsKey };
